import React, { useState, useEffect } from 'react';
import usePermissions from '../../hooks/usePermissions';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import dryBoxSurveyService from '../../services/DryBoxSurveyService.js';
import containerService from '../../services/ContainerService.js';
import userService from '../../services/UserService.js';
import { ClipboardCheck, ArrowLeft, Calendar } from "lucide-react";
import { useAuth } from '../../context/AuthContext.js';
import locationService from '../../services/LocationService.js';
import { shouldLockInspectorInEdit } from '../../utils/roleBasedUIHelpers.js';

const EditDryBoxSurvey = () => {
  const { surveyId } = useParams();
  const [formData, setFormData] = useState({
    client_id: '',
    container_id: '',
    date_of_inspection: '',
    place_of_inspection: '',
    status: 'ok',
    interior: '',
    remark: '',
    note: '',
    published: false,
    send_to_dm: false,
    surveyor_id: '',
    location_id: '',
    
    container_no: '',
    tare_weight: '',
    maximum_gross_weight: '',
    size: '',
    csc: '',
    csc_validity: '',
    date_of_mfg: '',
    depot_name: '',
    
    rear_end: 'ok',
    right_side: 'ok',
    front_side: 'ok',
    left_side: 'ok',
    top_roof: 'ok',
    under_structure: 'ok'
  });

  const [clients, setClients] = useState([]);
  const [containers, setContainers] = useState([]);
  const [surveyors, setSurveyors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [useExistingContainer, setUseExistingContainer] = useState(false);
  const { user } = useAuth();
  const {
    isSystemAdmin,
    isAdmin,
    isDocumentationManager,
    isSurveyInspector,
    isAccountant,
  } = usePermissions();
  const canEditPublishFields = isSystemAdmin || isAdmin || isDocumentationManager;
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
    fetchSurveyors();
    fetchLocations();
    fetchSurvey();
  }, [surveyId]);

  useEffect(() => {
    if (useExistingContainer && formData.client_id) {
      fetchClientContainers(formData.client_id);
    } else if (!useExistingContainer) {
      setContainers([]);
    }
  }, [formData.client_id, useExistingContainer]);

  useEffect(() => {
    if (useExistingContainer && formData.container_id) {
      loadContainerData(formData.container_id);
    }
  }, [formData.container_id, useExistingContainer]);

  const fetchClients = async () => {
    try {
      const response = await userService.getClients(null, null, { status: 'active' });
      setClients(response.data || []);
    } catch (err) {
      Alert.error('Failed to load clients');
      console.error(err);
    }
  };

  const fetchClientContainers = async (clientId) => {
    try {
      const response = await containerService.getByClientId(clientId);
      setContainers(response.data || []);
    } catch (err) {
      Alert.error('Failed to load containers');
      console.error(err);
    }
  };

  const fetchSurveyors = async () => {
    try {
      const response = await userService.getByRole(4);
      setSurveyors(response.data || []);
    } catch (err) {
      console.error('Failed to load surveyors:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations();
      setLocations(response.data || []);
    } catch (err) {
      Alert.error('Failed to load locations');
      console.error(err);
    }
  };

  const loadContainerData = async (containerId) => {
    try {
      const response = await containerService.getById(containerId);
      const container = response.data;
      
      setFormData(prev => ({
        ...prev,
        container_no: container.container_no || '',
        tare_weight: container.tare_weight || '',
        maximum_gross_weight: container.maximum_gross_weight || '',
        size: container.size || '',
        csc: container.csc || '',
        csc_validity: container.csc_validity || '',
        date_of_mfg: container.manufacture_date || '',
        depot_name: container.depot_name || ''
      }));
    } catch (err) {
      Alert.error('Failed to load container data');
      console.error(err);
    }
  };

  const handleUseExistingContainerToggle = (e) => {
    const checked = e.target.checked;
    setUseExistingContainer(checked);

    if (checked) {
      if (formData.client_id) {
        fetchClientContainers(formData.client_id);
      }
    } else {
      setContainers([]);
      setFormData(prev => ({
        ...prev,
        container_id: '',
        container_no: '',
        tare_weight: '',
        maximum_gross_weight: '',
        size: '',
        csc: '',
        csc_validity: '',
       date_of_mfg: '',
        depot_name: ''
      }));
    }
  };

  const fetchSurvey  = async () => {
    try {
      const response = await dryBoxSurveyService.getById(surveyId);
      const survey = response.data;
      
      setFormData({
        client_id: survey.client_id,
        container_id: survey.container_id,
        date_of_inspection: survey.date_of_inspection,
        place_of_inspection: survey.place_of_inspection || '',
        status: survey.status || 'ok',
        interior: survey.interior || '',
        remark: survey.remark || '',
        note: survey.note || '',
        published: survey.published,
        send_to_dm: survey.send_to_dm || false,
        surveyor_id: survey.surveyor_id,
        location_id: survey.location_id,
        
        container_no: survey.container_no || '',
        tare_weight: survey.tare_weight || '',
        maximum_gross_weight: survey.maximum_gross_weight || '',
        size: survey.size || '',
        csc: survey.csc || '',
        csc_validity: survey.csc_validity || '',
        date_of_mfg: survey.date_of_mfg || '',
        depot_name: survey.depot_name || '',
        
        rear_end: survey.rear_end || 'ok',
        right_side: survey.right_side || 'ok',
        front_side: survey.front_side || 'ok',
        left_side: survey.left_side || 'ok',
        top_roof: survey.top_roof || 'ok',
        under_structure: survey.under_structure || 'ok'
      });

      // If survey has a container_id, enable the existing container toggle
      if (survey.container_id) {
        setUseExistingContainer(true);
        if (survey.client_id) {
          fetchClientContainers(survey.client_id);
        }
      }
    } catch (err) {
      Alert.error('Failed to load survey data');
      console.error(err);
      navigate('/dry-box-surveys');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const cleanedData = { ...formData };
      
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '') {
          cleanedData[key] = null;
        }
      });

      await dryBoxSurveyService.update(surveyId, cleanedData);
      Alert.success('Dry box survey record updated successfully!');
      setTimeout(() => navigate('/dry-box-surveys'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update dry box survey record');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading survey data...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-6">
              <div className="max-w-8xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <ClipboardCheck className="w-5 h-5 text-buttonBg" /> Edit Dry Box Survey
                    </h1>
                  </div>
                  <button
                    onClick={() => navigate('/dry-box-surveys')}
                    className="relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-buttonBg text-buttonTxt font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:bg-transparent hover:text-buttonBg">
                    {/* COLLAPSING BACKGROUND */}
                    <span className="absolute inset-0 bg-buttonBg scale-x-100 group-hover:scale-x-0 origin-center transition-transform duration-300 ease-out z-0"/>
                    {/* ICON */}
                    <ArrowLeft className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90"/>
                    <span className="relative z-10">Back to Dry Box Survey List</span>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  {/* Section 1: Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Basic Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Client <span className="text-danger font-bold">*</span>
                        </label>
                        <select
                          name="client_id"
                          value={formData.client_id}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                          disabled
                        >
                          <option value="">Select Client</option>
                          {clients.map((client) => (
                            <option key={client.client_id} value={client.client_id}>
                              {client.name} - {client.organization_name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Client cannot be changed</p>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-1 sm:mb-2">
                          <input
                            type="checkbox"
                            id="use_existing_container"
                            checked={useExistingContainer}
                            onChange={handleUseExistingContainerToggle}
                            className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                            disabled={!formData.client_id}
                          />
                          <label htmlFor="use_existing_container" className="text-xs sm:text-sm font-medium text-secondary">
                            Use Existing Container
                          </label>
                        </div>
                        {useExistingContainer && (
                          <select
                            name="container_id"
                            value={formData.container_id}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                            required={useExistingContainer}
                            disabled={!formData.client_id}
                          >
                            <option value="">Select Container</option>
                            {containers.map((container) => (
                              <option key={container.id} value={container.id}>
                                {container.container_no || `Container ${container.id}`}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Date of Inspection <span className="text-danger font-bold">*</span>
                        </label>
                        <div className='relative'>
                          <input
                            type="date"
                            name="date_of_inspection"
                            value={formData.date_of_inspection}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                            required
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Surveyor <span className="text-danger font-bold">*</span>
                          {isSurveyInspector && <span className="text-blue-600 ml-2">(Locked)</span>}
                        </label>
                        <select
                          name="surveyor_id"
                          value={formData.surveyor_id}
                          onChange={handleChange}
                          disabled={isSurveyInspector}
                          className={`w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base ${isSurveyInspector ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          required
                        >
                          <option value="">Select Surveyor</option>
                          {surveyors.map((surveyor) => (
                            <option key={surveyor.user_id} value={surveyor.user_id}>
                              {surveyor.name}{isSurveyInspector && surveyor.user_id === user?.user_id ? ' (You)' : ''} - {surveyor.email}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Location <span className="text-danger font-bold">*</span>
                        </label>
                        <select
                          name="location_id"
                          value={formData.location_id}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Location</option>
                          {locations.map((location) => (
                            <option key={location.id} value={location.id}>
                              {location.city}, {location.state} ({location.city_code})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Place of Inspection</label>
                        <input
                          type="text"
                          name="place_of_inspection"
                          value={formData.place_of_inspection}
                          onChange={handleChange}
                          placeholder="Enter place of inspection"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Status <span className="text-danger font-bold">*</span>
                        </label>
                        <select
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                        >
                          <option value="ok">OK</option>
                          <option value="not ok">Not OK</option>
                        </select>
                      </div>

                      {/* Role-based checkboxes for Publish to Client and Send to DM */}
                      {(() => {
                        const showPublishCheckbox = canEditPublishFields;
                        const showSendToDMCheckbox = true;
                        const sendToDMDisabled = false;
                        const publishDisabled = !canEditPublishFields;
                        return (
                          <>
                            {showPublishCheckbox && (
                              <div className="flex items-center gap-2 mt-2">
                                <input
                                  type="checkbox"
                                  id="published"
                                  name="published"
                                  checked={formData.published}
                                  onChange={handleChange}
                                  className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                                  disabled={publishDisabled}
                                />
                                <label htmlFor="published" className="text-xs sm:text-sm font-medium text-secondary">
                                  Publish to Client
                                </label>
                              </div>
                            )}
                            {showSendToDMCheckbox && (
                              <div className="flex items-center gap-2 mt-2">
                                <input
                                  type="checkbox"
                                  id="send_to_dm"
                                  name="send_to_dm"
                                  checked={formData.send_to_dm}
                                  onChange={handleChange}
                                  className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                                  disabled={sendToDMDisabled}
                                />
                                <label htmlFor="send_to_dm" className="text-xs sm:text-sm font-medium text-secondary">
                                  Send to DM
                                </label>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Section 2: Container Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Container Details (From Selected Container)
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Container No</label>
                        <input
                          type="text"
                          name="container_no"
                          value={formData.container_no}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Tare Weight (Kg)</label>
                        <input
                          type="text"
                          name="tare_weight"
                          value={formData.tare_weight}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Maximum Gross Weight (Kg)</label>
                        <input
                          type="text"
                          name="maximum_gross_weight"
                          value={formData.maximum_gross_weight}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Size (Feet)</label>
                        <input
                          type="text"
                          name="size"
                          value={formData.size}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">CSC</label>
                        <input
                          type="text"
                          name="csc"
                          value={formData.csc}
                          onChange={handleChange}
                          placeholder="Auto-populated"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">CSC Validity</label>
                        <input
                          type="text"
                          name="csc_validity"
                          value={formData.csc_validity}
                          onChange={handleChange}
                          placeholder="Auto-populated"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Date of Manufacture</label>
                        <input
                          type="date"
                          name="date_of_mfg"
                          value={formData.date_of_mfg}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Depot Name</label>
                        <input
                          type="text"
                          name="depot_name"
                          value={formData.depot_name}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Side Inspections */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Side Inspections</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Rear End</label>
                        <select
                          name="rear_end"
                          value={formData.rear_end}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        >
                          <option value="ok">OK</option>
                          <option value="not ok">Not OK</option>
                          <option value="not sighted">Not Sighted</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Right Side</label>
                        <select
                          name="right_side"
                          value={formData.right_side}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        >
                          <option value="ok">OK</option>
                          <option value="not ok">Not OK</option>
                          <option value="not sighted">Not Sighted</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Front Side</label>
                        <select
                          name="front_side"
                          value={formData.front_side}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        >
                          <option value="ok">OK</option>
                          <option value="not ok">Not OK</option>
                          <option value="not sighted">Not Sighted</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Left Side</label>
                        <select
                          name="left_side"
                          value={formData.left_side}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        >
                          <option value="ok">OK</option>
                          <option value="not ok">Not OK</option>
                          <option value="not sighted">Not Sighted</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Top Roof</label>
                        <select
                          name="top_roof"
                          value={formData.top_roof}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        >
                          <option value="ok">OK</option>
                          <option value="not ok">Not OK</option>
                          <option value="not sighted">Not Sighted</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Under Structure</label>
                        <select
                          name="under_structure"
                          value={formData.under_structure}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        >
                          <option value="ok">OK</option>
                          <option value="not ok">Not OK</option>
                          <option value="not sighted">Not Sighted</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Inspection Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Inspection Details</h3>
                    <div className="grid grid-cols-1 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Interior</label>
                        <textarea
                          name="interior"
                          value={formData.interior}
                          onChange={handleChange}
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Remark</label>
                        <textarea
                          name="remark"
                          value={formData.remark}
                          onChange={handleChange}
                          rows="2"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Note</label>
                        <textarea
                          name="note"
                          value={formData.note}
                          onChange={handleChange}
                          rows="2"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          {/* ICON 
                          <Eye
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />*/}

                          <span className="relative z-10">{submitting ? 'Updating...' : 'Update Survey'}</span>
                      
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/dry-box-surveys')}
                      className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-white text-buttonBg
                        font-medium whitespace-nowrap

                        transition-colors duration-300 ease-out
                        hover:text-white
                      "
                    >
                      {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-0
                          group-hover:scale-x-100
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />
					            {/* ICON
                          <Pencil
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />*/}
                      <span className="relative z-10">Cancel</span>
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDryBoxSurvey;
